"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { playMenuHoverSound } from "@/hooks/use-sound-effects"

export interface RadialMenuOption {
  id: string
  icon: React.ReactNode
  label?: string
  sublabel?: React.ReactNode
  disabled?: boolean
  badge?: React.ReactNode
  onClick?: () => void
  backgroundColor?: string // Custom background color for this button
  borderColor?: string // Custom border color for this button
  tooltip?: {
    title: string
    description?: React.ReactNode
    titleColor?: string
    stats?: {
      damage?: string
      range?: number
      attackSpeed?: string
    }
  }
}

interface RadialMenuProps {
  position: { x: number; y: number }
  options: RadialMenuOption[]
  radius?: number
  iconSize?: number
  onClose?: () => void
  className?: string
  isMobile?: boolean
  itemClassName?: string
  noBorder?: boolean // Remove borders and rounded corners (for sprite-based icons)
}

export function RadialMenu({
  position,
  options,
  radius = 70,
  iconSize = 48,
  onClose,
  className,
  isMobile = false,
  itemClassName,
  noBorder = false,
}: RadialMenuProps) {
  const [visibleItems, setVisibleItems] = useState<number[]>([])
  const [hoveredOption, setHoveredOption] = useState<string | null>(null)
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null)

  // Staggered fade-in animation
  useEffect(() => {
    setVisibleItems([])
    options.forEach((_, index) => {
      setTimeout(() => {
        setVisibleItems((prev) => [...prev, index])
      }, index * 50) // 50ms stagger between each item
    })
  }, [options.length])

  // Calculate position for each option around the circle
  const getOptionPosition = (index: number, total: number) => {
    if (total === 1) {
      // Single option: place above center
      return { x: 0, y: -radius }
    }

    // Start from top (-90 degrees) and go clockwise
    const startAngle = -90
    const angleStep = 360 / total
    const angle = startAngle + index * angleStep
    const radians = (angle * Math.PI) / 180

    return {
      x: Math.cos(radians) * radius,
      y: Math.sin(radians) * radius,
    }
  }

  const handleContainerTouch = (e: React.TouchEvent) => {
    e.stopPropagation()
  }

  return (
    <>
      {/* Invisible backdrop to catch clicks outside the menu */}
      <div
        className="fixed inset-0 z-39"
        onClick={onClose}
        onTouchEnd={(e) => {
          e.preventDefault()
          onClose?.()
        }}
      />
      <div
        className={cn("fixed z-40 pointer-events-none", className)}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        onTouchStart={handleContainerTouch}
        onTouchMove={handleContainerTouch}
        onTouchEnd={handleContainerTouch}
      >
        <div className="relative">
        {options.map((option, index) => {
          const pos = getOptionPosition(index, options.length)
          const isVisible = visibleItems.includes(index)

          return (
            <div
              key={option.id}
              id={`tower-menu-${option.id}`}
              className="absolute pointer-events-auto"
              style={{
                left: `${pos.x}px`,
                top: `${pos.y}px`,
                transform: "translate(-50%, -50%)",
                opacity: isVisible ? 1 : 0,
                scale: isVisible ? 1 : 0.5,
                transition: "opacity 150ms ease-out, scale 150ms ease-out",
                zIndex: hoveredOption === option.id ? 100 : 1, // Elevate hovered item so tooltip shows above other buttons
              }}
            >
              <button
                onClick={() => {
                  if (!option.disabled) {
                    if (isMobile) {
                      if (selectedOptionId === option.id) {
                        // Second tap - execute action
                        option.onClick?.()
                        setSelectedOptionId(null)
                      } else {
                        // First tap - select and show info
                        setSelectedOptionId(option.id)
                      }
                    } else {
                      // Desktop - immediate execution
                      option.onClick?.()
                    }
                  }
                }}
                onMouseEnter={() => {
                  if (!isMobile && !option.disabled) {
                    playMenuHoverSound()
                  }
                  if (option.tooltip) {
                    setHoveredOption(option.id)
                  }
                }}
                onMouseLeave={() => setHoveredOption(null)}
                disabled={option.disabled}
                className={cn(
                  "relative flex items-center justify-center transition-transform",
                  !noBorder && "rounded-full",
                  !noBorder && (isMobile && selectedOptionId === option.id
                    ? "border-4 border-gray-700"
                    : !option.backgroundColor && (itemClassName || "bg-gradient-to-b from-amber-200 to-amber-400 border-4 border-amber-600")),
                  !noBorder && option.backgroundColor && "border-4",
                  !noBorder && "shadow-lg shadow-black/30",
                  noBorder && itemClassName,
                  option.disabled
                    ? "grayscale opacity-60 cursor-not-allowed"
                    : "hover:scale-110 active:scale-95 cursor-pointer"
                )}
                style={{
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  touchAction: "manipulation",
                  ...(isMobile && selectedOptionId === option.id
                    ? { backgroundColor: "#111827" } // gray-900
                    : option.backgroundColor
                      ? { backgroundColor: option.backgroundColor, borderColor: option.borderColor || option.backgroundColor }
                      : {}
                  ),
                }}
              >
                {option.badge && (
                  <div className="absolute -top-1 -right-1 z-10">
                    {option.badge}
                  </div>
                )}
                <div className="flex items-center justify-center w-full h-full">
                  {isMobile && selectedOptionId === option.id ? (
                    <svg
                      width={iconSize * 0.5}
                      height={iconSize * 0.5}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ filter: "drop-shadow(0 0 4px black) drop-shadow(0 0 2px black)" }}
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="#22c55e"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    option.icon
                  )}
                </div>
                {/* Tooltip - positioned below button (desktop only) */}
                {!isMobile && option.tooltip && hoveredOption === option.id && (
                  <div
                    className="absolute z-100 bg-black/95 border border-amber-600/50 rounded-lg px-3 py-2 pointer-events-none"
                    style={{
                      top: `${iconSize + 24}px`,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "180px",
                    }}
                  >
                    <div className="font-bold text-sm" style={{ color: option.tooltip.titleColor || "#f59e0b" }}>{option.tooltip.title}</div>
                    {option.tooltip.description && (
                      <div className="text-gray-300 text-xs leading-tight">{option.tooltip.description}</div>
                    )}
                    {option.tooltip.stats && (
                      <div className="mt-2 pt-2 border-t border-white/20 space-y-1">
                        {option.tooltip.stats.damage && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Damage</span>
                            <span className="text-red-400">{option.tooltip.stats.damage}</span>
                          </div>
                        )}
                        {option.tooltip.stats.attackSpeed && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Speed</span>
                            <span className="text-amber-400">{option.tooltip.stats.attackSpeed}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </button>

              {(option.label || option.sublabel) && (
                <div
                  className="flex flex-col items-center justify-center mt-1"
                  style={{ width: `${iconSize}px` }}
                >
                  {option.label && (
                    <span
                      className="text-game-gold font-bold text-sm leading-none"
                      style={{ textShadow: "0 0 3px black, 0 0 3px black" }}
                    >
                      {option.label}
                    </span>
                  )}
                  {option.sublabel}
                </div>
              )}
            </div>
          )
        })}

        {/* Mobile info panel - full-width bar at top of viewport */}
        {isMobile && selectedOptionId && (() => {
          const selected = options.find((o) => o.id === selectedOptionId)
          if (!selected?.tooltip) return null
          return (
            <div
              className="fixed z-[200] bg-black/95 border-b border-amber-600/50 px-4 py-3 pointer-events-none"
              style={{
                top: 0,
                left: 0,
                right: 0,
              }}
            >
              <div className="max-w-md mx-auto">
                <div className="font-bold text-sm mb-1" style={{ color: selected.tooltip.titleColor || "#f59e0b" }}>
                  {selected.tooltip.title}
                </div>
                {selected.tooltip.description && (
                  <div className="text-gray-300 text-xs leading-tight">{selected.tooltip.description}</div>
                )}
                {selected.tooltip.stats && (
                  <div className="mt-2 pt-2 border-t border-white/20 flex gap-6">
                    {selected.tooltip.stats.damage && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-400">Damage</span>
                        <span className="text-red-400">{selected.tooltip.stats.damage}</span>
                      </div>
                    )}
                    {selected.tooltip.stats.attackSpeed && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-400">Speed</span>
                        <span className="text-amber-400">{selected.tooltip.stats.attackSpeed}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })()}
      </div>
      </div>
    </>
  )
}
